import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, Timer, Loader2, ArrowRight } from 'lucide-react';
import { useT } from '../../utils/translate';
import DashSection from './DashSection';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const PERIODS = ['today', 'week', 'month'];

function periodRange(period) {
  const now = new Date();
  const start = new Date(now);
  if (period === 'week')       { start.setDate(now.getDate() - 6); start.setHours(0, 0, 0, 0); }
  else if (period === 'month') { start.setFullYear(now.getFullYear(), now.getMonth(), 1); start.setHours(0, 0, 0, 0); }
  else                         { start.setHours(0, 0, 0, 0); }
  return { from: start.toISOString(), to: now.toISOString() };
}

function fmtDur(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// ── KPI tile ─────────────────────────────────────────────────────────────────
function Kpi({ icon: Icon, tone, label, value, children }) {
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[13px] font-medium text-ink-secondary">{label}</span>
      </div>
      <p className="text-3xl font-bold text-ink leading-none tracking-tight">{value}</p>
      <div className="text-xs text-ink-tertiary min-h-[1rem] flex flex-wrap gap-x-3 gap-y-1">{children}</div>
    </div>
  );
}

const Dot = ({ className }) => <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${className}`} />;

// ── Stacked bar chart (incoming + outgoing), missed shown in the readout ─────
function CallsChart({ chart }) {
  const t = useT();
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...chart.map(b => b.incoming + b.outgoing));
  const step = Math.ceil(chart.length / 8);
  const hov = hover != null ? chart[hover] : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 min-h-[1.25rem]">
        <div className="flex items-center gap-4 text-xs text-ink-secondary">
          <span><Dot className="bg-primary-500" />{t('dashboardHome.legIn')}</span>
          <span><Dot className="bg-emerald-400" />{t('dashboardHome.legOut')}</span>
        </div>
        {hov && (
          <p className="text-xs text-ink-secondary">
            <span className="font-semibold text-ink">{hov.key}</span>
            {' · '}{t('dashboardHome.legIn')} {hov.incoming}
            {' · '}{t('dashboardHome.legOut')} {hov.outgoing}
            {' · '}<span className="text-red-600">{t('dashboardHome.legMissed')} {hov.missed}</span>
          </p>
        )}
      </div>
      <div className="flex items-end gap-1 h-40" onMouseLeave={() => setHover(null)}>
        {chart.map((b, i) => {
          const total = b.incoming + b.outgoing;
          return (
            <div key={b.key} className="flex-1 h-full flex flex-col justify-end min-w-0" onMouseEnter={() => setHover(i)}>
              <div className={`w-full flex flex-col justify-end rounded-t overflow-hidden transition-opacity ${hover != null && hover !== i ? 'opacity-50' : ''}`}
                style={{ height: `${(total / max) * 100}%`, minHeight: total ? 3 : 0 }}>
                <div className="bg-emerald-400" style={{ flex: b.outgoing }} />
                <div className="bg-primary-500" style={{ flex: b.incoming }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-2 border-t border-surface-100 pt-2">
        {chart.map((b, i) => (
          <div key={b.key} className="flex-1 min-w-0 text-center text-xs text-ink-tertiary leading-none">
            {i % step === 0 ? b.key : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Итоги исходящих: соединено / не ответил / занято / отменён / ошибка линии ─────────────
const OUT_ROWS = [['connected', 'bg-emerald-500'], ['no_answer', 'bg-amber-500'], ['busy', 'bg-orange-400'], ['cancelled', 'bg-slate-400'], ['failed', 'bg-red-500'], ['unknown', 'bg-slate-300']];
function OutResults({ data }) {
  const t = useT();
  const r = data.outResults || {};
  const total = Object.values(r).reduce((s, n) => s + n, 0);
  if (!total) return null;
  const crm = data.crmOut || { total: 0, connected: 0 };
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h3 className="text-sm font-semibold text-ink">{t('callPanel.stats.title')}</h3>
        {crm.total > 0 && <span className="text-xs text-ink-tertiary">{t('callPanel.stats.fromCrm', { n: crm.total, c: crm.connected })}</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
        {OUT_ROWS.filter(([k]) => r[k]).map(([k, bar]) => (
          <div key={k}>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-ink">{k === 'connected' ? t('callPanel.stats.connected') : t('callPanel.reason.' + k)}</span>
              <span className="text-xs text-ink-tertiary tabular-nums">{r[k]} · {Math.round((r[k] / total) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${bar}`} style={{ width: `${(r[k] / total) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main block ───────────────────────────────────────────────────────────────
export default function CallsOverview({ bare = false }) {
  const t = useT();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [userId, setUserId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios.get(`${API}/atc/summary`, {
      params: { period, ...periodRange(period), tz: new Date().getTimezoneOffset(), ...(userId ? { user: userId } : {}) },
    })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => {
        if (cancelled) return;
        if (e.response?.status === 403) setDenied(true);   // rolda "Qo'ng'iroqlar" moduli yo'q — blok yashiriladi
        else setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, userId]);

  const employees = useMemo(() => (data?.byUser || []).filter(u => u.userId), [data]);
  const maxEmp = Math.max(1, ...(data?.byUser || []).map(u => u.total));

  if (denied) return null;

  const tt = data?.totals;

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex p-0.5 bg-surface-100 rounded-lg max-w-full overflow-x-auto no-scrollbar">
        {PERIODS.map(p => (
          <button key={p} type="button" onClick={() => setPeriod(p)}
            className={`px-3 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${period === p ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
            {t('dashboardHome.p' + p.charAt(0).toUpperCase() + p.slice(1))}
          </button>
        ))}
      </div>
      <select className="input !w-auto !py-1.5 !text-xs min-w-[10rem]" value={userId} onChange={e => setUserId(e.target.value)}>
        <option value="">{t('dashboardHome.allEmployees')}</option>
        {employees.map(u => <option key={u.userId} value={u.userId}>{u.name || '—'}</option>)}
      </select>
      {!bare && (
        <button type="button" onClick={() => navigate('/calls')} className="btn-ghost btn-sm !text-xs text-primary-600 flex items-center gap-1">
          {t('dashboardHome.openCalls')} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <DashSection title={bare ? null : t('dashboardHome.secCalls')} subtitle={bare ? null : t('dashboardHome.secCallsSub')} actions={toolbar}>
      {error ? (
        <div className="bg-white rounded-2xl border border-surface-200 p-8 text-center text-sm text-ink-tertiary">{t('dashboardHome.callsLoadError')}</div>
      ) : !tt ? (
        <div className="bg-white rounded-2xl border border-surface-200 p-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary-400" />
        </div>
      ) : tt.total === 0 && !userId ? (
        // Нулевой период: одна строка с действием вместо пяти нулевых плиток и двух пустых блоков
        <div className="bg-white rounded-2xl border border-surface-200 px-5 py-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-secondary">{t('dashboardHome.noCalls')}</p>
          {period !== 'month' && (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setPeriod(period === 'today' ? 'week' : 'month')}>
              {t('dashboardHome.showPeriod', { p: t('dashboardHome.p' + (period === 'today' ? 'Week' : 'Month')) })}
            </button>
          )}
        </div>
      ) : (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi icon={PhoneCall} tone="bg-primary-50 text-primary-600" label={t('dashboardHome.cTotal')} value={tt.total}>
              <span>{t('dashboardHome.cTalk', { t: fmtDur(tt.talkTime) })}</span>
            </Kpi>
            <Kpi icon={PhoneIncoming} tone="bg-blue-50 text-blue-600" label={t('dashboardHome.cIn')} value={tt.incoming}>
              <span><Dot className="bg-emerald-500" />{t('dashboardHome.cAnswered', { n: tt.inAnswered })}</span>
              <span><Dot className="bg-red-500" />{t('dashboardHome.cMissedN', { n: tt.inMissed })}</span>
            </Kpi>
            <Kpi icon={PhoneOutgoing} tone="bg-emerald-50 text-emerald-700" label={t('dashboardHome.cOut')} value={tt.outgoing}>
              <span><Dot className="bg-emerald-500" />{t('dashboardHome.cAnswered', { n: tt.outAnswered })}</span>
              <span><Dot className="bg-red-500" />{t('dashboardHome.cNoAnswerN', { n: tt.outMissed })}</span>
            </Kpi>
            <Kpi icon={PhoneMissed} tone="bg-red-50 text-red-600" label={t('dashboardHome.cMissed')} value={tt.missed}>
              <span>{t('dashboardHome.cMissedPct', { n: tt.missedPct })}</span>
            </Kpi>
            <Kpi icon={Timer} tone="bg-amber-50 text-amber-700" label={t('dashboardHome.cAvg')} value={fmtDur(tt.avgDuration)} />
          </div>

          {tt.outgoing > 0 && <OutResults data={data} />}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 bg-white rounded-2xl border border-surface-200 p-5">
              <h3 className="text-sm font-semibold text-ink mb-4">{t('dashboardHome.chartTitle')}</h3>
              {tt.total === 0
                ? <p className="py-16 text-center text-sm text-ink-tertiary">{t('dashboardHome.noCalls')}</p>
                : <CallsChart chart={data.chart} />}
            </div>

            <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-200 overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-surface-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink">{t('dashboardHome.empTitle')}</h3>
                <div className="flex gap-4 text-xs text-ink-tertiary">
                  <span className="w-8 text-right">{t('dashboardHome.thIn')}</span>
                  <span className="w-8 text-right">{t('dashboardHome.thOut')}</span>
                  <span className="w-10 text-right">{t('dashboardHome.thMissed')}</span>
                </div>
              </div>
              <div className="divide-y divide-surface-100 overflow-y-auto max-h-64">
                {data.byUser.length === 0 && <p className="py-10 text-center text-sm text-ink-tertiary">{t('dashboardHome.noCalls')}</p>}
                {data.byUser.map(u => {
                  const active = userId && userId === u.userId;
                  const name = u.name || t('dashboardHome.noEmployee');
                  return (
                    <button key={u.userId || 'none'} type="button" disabled={!u.userId}
                      onClick={() => setUserId(active ? '' : u.userId)}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${active ? 'bg-primary-50' : u.userId ? 'hover:bg-surface-50' : ''}`}>
                      <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center shrink-0">
                        {name.charAt(0).toUpperCase()}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-ink truncate">{name}</span>
                          <span className="text-xs text-ink-tertiary shrink-0">{fmtDur(u.talkTime)}</span>
                        </span>
                        <span className="block h-1.5 mt-1.5 bg-surface-100 rounded-full overflow-hidden">
                          <span className="block h-full bg-primary-500 rounded-full" style={{ width: `${(u.total / maxEmp) * 100}%` }} />
                        </span>
                      </span>
                      <span className="flex gap-4 text-sm tabular-nums shrink-0">
                        <span className="w-8 text-right text-ink">{u.incoming}</span>
                        <span className="w-8 text-right text-ink">{u.outgoing}</span>
                        <span className={`w-10 text-right ${u.inMissed + u.outMissed ? 'text-red-600 font-semibold' : 'text-ink-tertiary'}`}>{u.inMissed + u.outMissed}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashSection>
  );
}
